import React, { useState } from 'react';
import { apiClient } from 'Utils/api';
import Header from 'View/Component/Header/Header';
import Footer from 'View/Component/Footer/Footer';
import PageContentRenderer from 'View/Component/PageContentRenderer/PageContentRenderer';
import * as S from '../SellPage/SellPage.style';

const CONDITION_OPTIONS: { value: string; label: string }[] = [
  { value: 'NEW_UNWORN', label: 'Neu / Ungetragen' },
  { value: 'USED_GOOD', label: 'Gebraucht, guter Zustand' },
  { value: 'ANY', label: 'Beides möglich, sollte aber zum Preis passen' },
];

interface FormData {
  brand: string;
  model: string;
  referenceNumber: string;
  budget: string;
  condition: string;
  notes: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  privacyAccepted: boolean;
}

const initialForm: FormData = {
  brand: '',
  model: '',
  referenceNumber: '',
  budget: '',
  condition: '',
  notes: '',
  customerName: '',
  customerEmail: '',
  customerPhone: '',
  privacyAccepted: false,
};

interface SectionProps {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  testId?: string;
}

const AccordionSection: React.FC<SectionProps> = ({ title, open, onToggle, children, testId }) => (
  <S.AccordionItem data-testid={testId}>
    <S.AccordionHeader type="button" onClick={onToggle} aria-expanded={open}>
      <span>{title}</span>
      <S.AccordionChevron open={open}>›</S.AccordionChevron>
    </S.AccordionHeader>
    {open && <S.AccordionBody>{children}</S.AccordionBody>}
  </S.AccordionItem>
);

const RequestPage: React.FC = () => {
  const [form, setForm] = useState<FormData>(initialForm);
  const [openSection, setOpenSection] = useState<string>('watch');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const setField = <K extends keyof FormData>(key: K, value: FormData[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const toggle = (key: string) =>
    setOpenSection((prev) => (prev === key ? '' : key));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.brand.trim() || !form.model.trim()) {
      setError('Bitte geben Sie Hersteller und Modell an.');
      setOpenSection('watch');
      return;
    }
    if (!form.customerName.trim() || !form.customerEmail.trim()) {
      setError('Bitte geben Sie Ihren Namen und Ihre E-Mail-Adresse an.');
      setOpenSection('contact');
      return;
    }
    if (!form.privacyAccepted) {
      setError('Bitte bestätigen Sie die Datenschutzerklärung.');
      setOpenSection('contact');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        brand: form.brand.trim(),
        model: form.model.trim(),
        referenceNumber: form.referenceNumber.trim() || undefined,
        budgetCents: form.budget
          ? Math.round(parseFloat(form.budget.replace(/\./g, '').replace(',', '.')) * 100)
          : undefined,
        condition: form.condition || undefined,
        notes: form.notes.trim() || undefined,
        customerName: form.customerName.trim(),
        customerEmail: form.customerEmail.trim(),
        customerPhone: form.customerPhone.trim() || undefined,
        privacyAccepted: true,
      };
      const res = await apiClient.post('/api/search-requests', payload);
      if (res.data?.status === 'success') {
        setSuccess(true);
        setForm(initialForm);
      } else {
        setError(res.data?.message || 'Beim Senden ist ein Fehler aufgetreten.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Beim Senden ist ein Fehler aufgetreten.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <>
        <Header />
        <S.PageContainer>
          <S.SuccessBox data-testid="search-success">
            <S.SuccessIcon>✓</S.SuccessIcon>
            <S.SuccessTitle>Vielen Dank für Ihre Suchanfrage!</S.SuccessTitle>
            <S.SuccessText>
              Wir haben Ihre Anfrage erhalten und melden uns zeitnah persönlich bei Ihnen,
              sobald wir ein passendes Modell für Sie haben.
            </S.SuccessText>
            <S.PrimaryButton
              type="button"
              onClick={() => setSuccess(false)}
              data-testid="search-new-request-btn"
            >
              Neue Suchanfrage stellen
            </S.PrimaryButton>
          </S.SuccessBox>
        </S.PageContainer>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <S.PageContainer>
        <S.Headline data-testid="search-headline">Suchanfrage</S.Headline>

        <PageContentRenderer slug="anfragen" />

        <S.FormCard onSubmit={handleSubmit} data-testid="search-form">
          <AccordionSection
            title="Welche Uhr suchen Sie?"
            open={openSection === 'watch'}
            onToggle={() => toggle('watch')}
            testId="search-section-watch"
          >
            <S.Row>
              <S.Field>
                <S.Label>Hersteller *</S.Label>
                <S.Input
                  data-testid="search-input-brand"
                  value={form.brand}
                  onChange={(e) => setField('brand', e.target.value)}
                  placeholder="z. B. Rolex, Patek Philippe, Omega"
                />
              </S.Field>
              <S.Field>
                <S.Label>Modell *</S.Label>
                <S.Input
                  data-testid="search-input-model"
                  value={form.model}
                  onChange={(e) => setField('model', e.target.value)}
                  placeholder="z. B. Submariner, Nautilus, Speedmaster"
                />
              </S.Field>
            </S.Row>
            <S.Row>
              <S.Field>
                <S.Label>Ref-Nummer / Modellnummer</S.Label>
                <S.Input
                  data-testid="search-input-reference"
                  value={form.referenceNumber}
                  onChange={(e) => setField('referenceNumber', e.target.value)}
                  placeholder="z. B. 126610LV"
                />
              </S.Field>
              <S.Field>
                <S.Label>Verfügbares Budget (EUR)</S.Label>
                <S.Input
                  data-testid="search-input-budget"
                  type="text"
                  inputMode="decimal"
                  value={form.budget}
                  onChange={(e) => setField('budget', e.target.value)}
                  placeholder="z. B. 15.000"
                />
              </S.Field>
            </S.Row>
            <S.Field>
              <S.Label>Zustand</S.Label>
              <S.Select
                data-testid="search-input-condition"
                value={form.condition}
                onChange={(e) => setField('condition', e.target.value)}
              >
                <option value="">Bitte auswählen</option>
                {CONDITION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </S.Select>
            </S.Field>
            <S.Field>
              <S.Label>
                Gibt es Besonderheiten, die Sie uns mitteilen möchten?
              </S.Label>
              <S.Textarea
                data-testid="search-input-notes"
                rows={4}
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
                placeholder="z. B. Zifferblattfarbe, Bandausführung, Deadline wegen Geburtstag, bestimmte Ausführungen oder Baujahre …"
              />
            </S.Field>
          </AccordionSection>

          <AccordionSection
            title="Ihre Kontaktdaten"
            open={openSection === 'contact'}
            onToggle={() => toggle('contact')}
            testId="search-section-contact"
          >
            <S.Field>
              <S.Label>Name *</S.Label>
              <S.Input
                data-testid="search-input-name"
                value={form.customerName}
                onChange={(e) => setField('customerName', e.target.value)}
              />
            </S.Field>
            <S.Row>
              <S.Field>
                <S.Label>E-Mail *</S.Label>
                <S.Input
                  data-testid="search-input-email"
                  type="email"
                  value={form.customerEmail}
                  onChange={(e) => setField('customerEmail', e.target.value)}
                />
              </S.Field>
              <S.Field>
                <S.Label>Telefon</S.Label>
                <S.Input
                  data-testid="search-input-phone"
                  value={form.customerPhone}
                  onChange={(e) => setField('customerPhone', e.target.value)}
                  placeholder="optional"
                />
              </S.Field>
            </S.Row>
            <S.CheckboxRow>
              <S.CheckboxLabel>
                <input
                  data-testid="search-input-privacy"
                  type="checkbox"
                  checked={form.privacyAccepted}
                  onChange={(e) => setField('privacyAccepted', e.target.checked)}
                />
                Ich habe die <a href="/datenschutz" target="_blank" rel="noreferrer">Datenschutzerklärung</a> zur Kenntnis genommen und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu. *
              </S.CheckboxLabel>
            </S.CheckboxRow>
          </AccordionSection>

          {error && <S.ErrorMessage data-testid="search-error">{error}</S.ErrorMessage>}

          <S.Actions>
            <S.PrimaryButton
              type="submit"
              disabled={submitting || !form.privacyAccepted}
              data-testid="search-submit-btn"
            >
              {submitting ? 'Wird gesendet …' : 'Anfrage absenden'}
            </S.PrimaryButton>
          </S.Actions>
        </S.FormCard>
      </S.PageContainer>
      <Footer />
    </>
  );
};

export default RequestPage;
